import {gql} from '@apollo/client';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {MethodNode} from 'procedures/ProcedureDomainTypes';

import {ArchivedLibraryParameterDefinitionsQuery} from './__generated/ArchivedLibraryParameterDefinitionsQuery';

// Stable reference to avoid unnecessary re-renders when callers include the result in dependency arrays.
const EMPTY_ARCHIVED_SET: Set<string> = new Set();

export const ARCHIVED_LIBRARY_PARAMETER_DEFINITIONS_QUERY = gql`
  query ArchivedLibraryParameterDefinitionsQuery($ids: [StrictString!]!)
  @skipAudit
  @engComponent(name: PROCEDURES) {
    libraryParameterDefinitions(ids: $ids) {
      id
      archiveData {
        archived
      }
    }
  }
`;

/**
 * Extracts unique library parameter definition IDs from method nodes.
 */
export function getLibraryParameterDefinitionIdsFromMethods(methodNodes: Array<MethodNode>): Array<string> {
  const ids = new Set<string>();
  for (const methodNode of methodNodes) {
    for (const setting of methodNode.dataParameterSettings) {
      const libDef = setting.dataParameterVersion.libraryParameterDefinition;
      if (libDef != null) {
        ids.add(libDef.id);
      }
    }
  }
  return [...ids];
}

/**
 * Fetches archive status for the given library parameter definition IDs and
 * returns the set of IDs that are archived. Returns null while loading or if the underlying query is not
 * successful.
 */
export default function useArchivedLibraryParameterDefinitionIds(
  libraryParameterDefinitionIds: Array<string>
): Set<string> | null {
  const result = useSkippableBenchlingQuery<ArchivedLibraryParameterDefinitionsQuery>(
    ARCHIVED_LIBRARY_PARAMETER_DEFINITIONS_QUERY,
    {
      variables: {ids: libraryParameterDefinitionIds},
      skip: libraryParameterDefinitionIds.length === 0,
    }
  );

  if (libraryParameterDefinitionIds.length === 0) {
    return EMPTY_ARCHIVED_SET;
  }

  if (result.type === QueryResultType.SUCCESS) {
    const archivedIds = new Set<string>();
    for (const def of result.data.libraryParameterDefinitions) {
      if (def.archiveData.archived) {
        archivedIds.add(def.id);
      }
    }
    return archivedIds;
  }

  return null;
}
